import { v4 as uuid } from 'uuid';
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatModels from "@medsurf/flat-models";
import * as FlatFacades from '../../internal';

@Injectable()
export class KeymapLabelEntityFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  static getNewInstance(id: string,
                        keymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn,
                        index: string,
                        text: string) {
    const keymapLabel: FlatModels.KeymapLabelEntityModels.KeymapLabel = {
      id,
      order: keymapColumn.labels.findIndex(l => l === id),
      index,
      text,
      column: keymapColumn.id,
      annotation: uuid(),
    };
    return keymapLabel;
  }
  
  static addKeymapLabel(store: Store,
                        id: string,
                        page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                        image: FlatModels.ImageEntityModels.Image | null,
                        poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                        keymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn,
                        index: string,
                        text: string,
                        currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Get New Instance
    const keymapLabel = KeymapLabelEntityFacade.getNewInstance(id, keymapColumn, index, text);
    
    // Create Keymap Label Pipe Object
    const keymapLabelPipeModel: FlatModels.PipeEntityModels.KeymapLabelPipeObject = {
      data: {},
      keymapLabel,
    }
    
    // Add Keymap Label
    return of(keymapLabelPipeModel).pipe(
      mergeMap((result: FlatModels.PipeEntityModels.KeymapLabelPipeObject) =>
        FlatFacades.AnnotationEntityFacade.addMarker(store, keymapLabel.annotation!, page, image, poi, FlatModels.AnnotationEntityModels.TipType.enum.dot, currentPositionAndZoom, index, false, true).pipe(
          mergeMap((data) => of(Object.assign(result, data))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.KeymapLabelPipeObject) => {
        return store.dispatch(new FlatActions.KeymapLabelEntityActions.AddEntity(keymapLabel)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        )
      }),
    );
  }
  
  static getCopyInstance(store: Store,
                         id: string,
                         keymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn) {
    // Get Data
    const typedKeymapLabels = store.selectSnapshot(FlatStates.KeymapLabelEntityState.typedEntities$);
    
    // Get Keymap Label
    const keymapLabel = structuredClone(typedKeymapLabels[id]);
    
    // Update Keymap Label
    keymapLabel.id = uuid();
    keymapLabel.column = keymapColumn.id;
    
    return keymapLabel
  }
  
  static copyKeymapLabel(store: Store,
                         id: string,
                         keymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn,
                         image: FlatModels.ImageEntityModels.Image | null,
                         clusterParentUpdate = false) {
    // Get Data
    const typedKeymapLabels = store.selectSnapshot(FlatStates.KeymapLabelEntityState.typedEntities$);
    
    // Get Keymap Label
    const keymapLabel = structuredClone(typedKeymapLabels[id]);
    
    // Update Keymap Label
    keymapLabel.id = uuid();
    
    // Update Keymap Column
    keymapColumn.labels.push(keymapLabel.id);
    
    // Copy KeymapLabel
    return of({}).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.KeymapLabelEntityActions.AddEntity(keymapLabel))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.KeymapColumnEntityActions.UpdateEntity(keymapColumn));
      }),
    );
  }
  
  static copyKeymapLabels(store: Store,
                          ids: string[],
                          keymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn,
                          image: FlatModels.ImageEntityModels.Image | null,
                          clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Copy Keymap Labels
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => KeymapLabelEntityFacade.copyKeymapLabel(store, id, keymapColumn, image,true)))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.KeymapColumnEntityActions.UpdateEntity(keymapColumn));
      }),
    );
  }
  
  static removeKeymapLabel(store: Store,
                           id: string,
                           clusterParentUpdate = false) {
    // Get Data
    const typedKeymapLabels = store.selectSnapshot(FlatStates.KeymapLabelEntityState.typedEntities$);
    const typedKeymapColumns = store.selectSnapshot(FlatStates.KeymapColumnEntityState.typedEntities$);
    
    // Get Keymap Label
    const keymapLabel = structuredClone(typedKeymapLabels[id]);
    
    // Get Keymap Column
    const keymapColumn = structuredClone(typedKeymapColumns[keymapLabel.column]) as FlatModels.KeymapColumnEntityModels.KeymapColumn;
    keymapColumn.labels.splice(keymapColumn.labels.findIndex(id => id === keymapLabel.id), 1);
    
    // Remove Keymap Label
    return store.dispatch(new FlatActions.KeymapLabelEntityActions.RemoveEntity(keymapLabel)).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data, keymapColumn});
        }
        return store.dispatch(new FlatActions.KeymapLabelEntityActions.RearrangeKeymapLabels(keymapColumn.labels)).pipe(
          mergeMap(() =>
            store.dispatch(new FlatActions.KeymapColumnEntityActions.UpdateEntity(keymapColumn))
          )
        );
      }),
    );
  }
  
  static removeKeymapLabels(store: Store,
                            ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Remove Keymap Labels
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => KeymapLabelEntityFacade.removeKeymapLabel(store, id, true)))
      ),
      mergeMap((data: {data: Store, keymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn}[]) => {
        const keymapColumnMap = data.reduce((arr, clusterParentUpdate) => {
          const keymapColumn = structuredClone(arr.get(clusterParentUpdate.keymapColumn.id));
          if (keymapColumn) {
            keymapColumn.labels = clusterParentUpdate.keymapColumn.labels.filter((id: string) => keymapColumn.labels.includes(id));
            arr.set(clusterParentUpdate.keymapColumn.id, keymapColumn);
          } else {
            arr.set(clusterParentUpdate.keymapColumn.id, clusterParentUpdate.keymapColumn);
          }
          return arr;
        }, new Map<string, FlatModels.KeymapColumnEntityModels.KeymapColumn>());
        const keymapColumns = Array.from(keymapColumnMap.values());
        if (keymapColumns.length > 0) {
          return forkJoin(keymapColumns.map((keymapColumn) =>
            store.dispatch(new FlatActions.KeymapLabelEntityActions.RearrangeKeymapLabels(keymapColumn.labels)).pipe(
              mergeMap(() =>
                store.dispatch(new FlatActions.KeymapColumnEntityActions.UpdateEntity(keymapColumn))
              )
            )
          ));
        }
        return of(data);
      }),
    );
  }

  //</editor-fold>
}
